import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PartnerPortalPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/partner-portal/${token}`);
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Lien invalide ou expiré.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <Shell><p className="text-zinc-400">Chargement…</p></Shell>;
  if (error) return <Shell><p className="text-red-300" data-testid="portal-error">⚠️ {error}</p></Shell>;

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("fr-CA"); } catch (e) { return iso; } };

  return (
    <Shell>
      <div data-testid="partner-portal">
        <div className="text-[10px] tracking-[0.4em] text-amber-400 uppercase">Réseau Kaizen · Portail partenaire</div>
        <h1 className="text-3xl font-bold text-zinc-100 mt-1">{data.name}</h1>
        <p className="text-zinc-400 text-sm mt-1">
          {[data.school, data.city].filter(Boolean).join(" · ")}{data.status ? ` · ${data.status}` : ""}
        </p>
        {data.disciplines && <p className="text-zinc-500 text-sm mt-0.5">Disciplines : {data.disciplines}</p>}
        {data.next_meeting_date && <p className="text-sky-400 text-sm mt-2">📅 Prochaine visite prévue : {data.next_meeting_date}</p>}
        <p className="text-[11px] text-zinc-600 mt-2">Vue en lecture seule — mise à jour automatiquement par ton mentor.</p>

        <section className="mt-8">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-zinc-100">🥋 Mes élèves suivis ({data.students.length})</h2>
            {data.students.length > 0 && <span className="text-sm text-amber-300">Progression moy. {data.students_avg_pct}%</span>}
          </div>
          <div className="mt-3 space-y-2">
            {data.students.map((s, i) => (
              <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`portal-student-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-zinc-100">{s.name}</div>
                    <div className="text-xs text-zinc-500">{[s.belt, s.discipline].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <span className="text-sm font-bold text-amber-300">{s.progress_pct}%</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, s.progress_pct)}%` }} />
                </div>
              </div>
            ))}
            {data.students.length === 0 && <p className="text-sm text-zinc-500">Aucun élève suivi pour l'instant.</p>}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-lg font-semibold text-zinc-100">📄 Mes conventions ({data.contracts.length})</h2>
          <div className="mt-3 space-y-2">
            {data.contracts.map((c, i) => (
              <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 flex items-center justify-between" data-testid={`portal-contract-${i}`}>
                <div>
                  <div className="text-sm text-zinc-100">Convention · {fmt(c.generated_at)}</div>
                  {c.signed
                    ? <div className="text-[11px] text-emerald-400">✅ Signée{c.signed_at ? ` · ${fmt(c.signed_at)}` : ""}</div>
                    : <div className="text-[11px] text-amber-400">✍️ En attente de signature</div>}
                </div>
                <a href={`${BACKEND}${c.url}`} target="_blank" rel="noreferrer" className="text-xs text-sky-400 hover:underline">Ouvrir le PDF</a>
              </div>
            ))}
            {data.contracts.length === 0 && <p className="text-sm text-zinc-500">Aucune convention disponible pour l'instant.</p>}
          </div>
        </section>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="mb-8 text-[11px] tracking-[0.3em] text-zinc-600 uppercase">Institut Impact Élite Martial × Kaizen System</div>
        {children}
      </div>
    </div>
  );
}
